/**
 * Formats a date string to a relative time format (e.g. "5 mins ago").
 * Falls back to standard date format for older dates.
 * Handles both ISO 8601 date strings and Eastern Time format strings.
 */
export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try to parse the date directly first (works with ISO 8601 strings)
    date = new Date(dateString);

    // If the date is invalid, try parsing with Eastern Time assumption
    if (isNaN(date.getTime())) {
      // For legacy Eastern Time format handling
      date = new Date(dateString + ' EST');
    }

    // If still invalid, return fallback
    if (isNaN(date.getTime())) {
      console.warn('Unable to parse date:', dateString);
      return dateString; // Return original string as fallback
    }

    // Get the current time in the user's timezone
    const now = new Date();

    // Calculate the time difference in seconds
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Less than a minute
    if (diffInSeconds < 60) {
      return 'Just now';
    }

    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
    }

    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }

    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    // Less than a month (approximated as 30 days)
    if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }

    // More than a month, use standard date format
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (e) {
    console.error('Error formatting relative time:', e);
    return dateString; // Return the original string if parsing fails
  }
}
